import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { maIroScoringColors } from 'constants/colors';

import type { Plugin } from 'chart.js';
import type IChartDataPoint from '../interfaces/IChartDataPoint';
import type { IActiveElement, IChartHoverEvent } from '../interfaces/IChartEventTypes';
import type IUseMAIroChart from '../interfaces/IUseMAIroChart';

const useMAIroChart = ({ items }: IUseMAIroChart) => {
  const { t } = useTranslation();
  const chartData = useMemo(() => {
    const opportunityPoints = items
      .filter((item) => item.impact > 0)
      .map((item, index) => ({
        x: item.financial,
        y: item.impact,
        pointBackgroundColor: maIroScoringColors.tooltipValueColor,
        pointBorderColor: maIroScoringColors.tooltipValueColor,
        pointRadius: 12,
        pointHoverRadius: 14,
        label: (index + 1).toString(),
        item,
      }));

    const riskPoints = items
      .filter((item) => item.impact <= 0)
      .map((item, index) => ({
        x: item.financial,
        y: item.impact,
        pointBackgroundColor: maIroScoringColors.riskRed,
        pointBorderColor: maIroScoringColors.riskRed,
        pointRadius: 12,
        pointHoverRadius: 14,
        label: (opportunityPoints.length + index + 1).toString(),
        item,
      }));

    return {
      datasets: [
        {
          label: t('materialityAssessment.opportunities'),
          data: opportunityPoints,
          backgroundColor: maIroScoringColors.opportunityGreen,
          borderColor: maIroScoringColors.opportunityGreen,
          pointBackgroundColor: opportunityPoints.map((p) => p.pointBackgroundColor),
          pointBorderColor: opportunityPoints.map((p) => p.pointBorderColor),
          pointRadius: opportunityPoints.map((p) => p.pointRadius),
          pointHoverRadius: opportunityPoints.map((p) => p.pointHoverRadius),
        },
        {
          label: t('materialityAssessment.risks'),
          data: riskPoints,
          backgroundColor: maIroScoringColors.riskRed,
          borderColor: maIroScoringColors.riskRed,
          pointBackgroundColor: riskPoints.map((p) => p.pointBackgroundColor),
          pointBorderColor: riskPoints.map((p) => p.pointBorderColor),
          pointRadius: riskPoints.map((p) => p.pointRadius),
          pointHoverRadius: riskPoints.map((p) => p.pointHoverRadius),
        },
      ],
    };
  }, [items, t]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        type: 'linear' as const,
        position: 'bottom' as const,
        min: -50,
        max: 50,
        border: {
          display: true,
          color: maIroScoringColors.chartBorder,
          width: 1,
        },
        grid: {
          display: true,
          color: maIroScoringColors.tooltipBorder,
          drawOnChartArea: true,
        },
        ticks: {
          stepSize: 1,
          font: {
            size: 12,
          },
        },
        title: {
          display: true,
          text: t('materialityAssessment.financial'),
          font: {
            size: 14,
            weight: 'bold' as const,
          },
        },
      },
      y: {
        type: 'linear' as const,
        min: -70,
        max: 70,
        border: {
          display: true,
          color: maIroScoringColors.chartBorder,
          width: 1,
        },
        grid: {
          display: true,
          color: maIroScoringColors.tooltipBorder,
          drawOnChartArea: true,
        },
        ticks: {
          stepSize: 10,
          font: {
            size: 12,
          },
        },
        title: {
          display: true,
          text: t('materialityAssessment.impact'),
          font: {
            size: 14,
            weight: 'bold' as const,
          },
        },
      },
    },
    onHover: (event: IChartHoverEvent, activeElements: IActiveElement[]) => {
      const tooltipEl = document.getElementById('chartjs-tooltip') || (() => {
        const div = document.createElement('div');
        div.id = 'chartjs-tooltip';
        div.style.cssText = `
          position: fixed;
          background: ${maIroScoringColors.tooltipBackground};
          border: 1px solid ${maIroScoringColors.tooltipBorder};
          border-radius: 8px;
          color: ${maIroScoringColors.tooltipTextColor};
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          padding: 12px 16px;
          pointer-events: none;
          transition: opacity 0.1s ease;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          z-index: 1000;
          max-width: 400px;
          min-width: 250px;
          word-wrap: break-word;
          word-break: break-word;
          white-space: normal;
          hyphens: auto;
          opacity: 0;
        `;

        if (!div.querySelector('.tooltip-arrow')) {
          const arrow = document.createElement('div');
          arrow.className = 'tooltip-arrow';
          arrow.style.cssText = `
            position: absolute;
            left: -8px;
            top: 50%;
            transform: translateY(-50%);
            width: 0;
            height: 0;
            border-top: 8px solid transparent;
            border-bottom: 8px solid transparent;
          `;
          div.appendChild(arrow);

          const arrowInner = document.createElement('div');
          arrowInner.style.cssText = `
            position: absolute;
            left: 1px;
            top: -7px;
            width: 0;
            height: 0;
            border-top: 7px solid transparent;
            border-bottom: 7px solid transparent;
            border-right: 7px solid ${maIroScoringColors.tooltipBackground};
          `;
          arrow.appendChild(arrowInner);
        }
        document.body.appendChild(div);
        return div;
      })();

      if (activeElements.length === 0) {
        tooltipEl.style.opacity = '0';
        setTimeout(() => {
          if (tooltipEl.style.opacity === '0') {
            tooltipEl.innerHTML = '';
          }
        }, 150);
        return;
      }

      const activeElement = activeElements[0];
      const dataPoint = event.chart.data.datasets[activeElement.datasetIndex]
        .data[activeElement.index] as IChartDataPoint;

      if (dataPoint?.item) {
        const {
          title, subtitle, description, impact, financial,
        } = dataPoint.item;

        const formattedDescription = description;

        const titleStyle = `color: ${maIroScoringColors.tooltipTitleColor}; font-weight: 600; font-size: 14px; `
          + 'margin-bottom: 8px; padding-bottom: 4px;';
        const subtitleStyle = `color: ${maIroScoringColors.tooltipTextColor}; font-size: 12px; margin-bottom: 8px;`;
        const descStyle = `color: ${maIroScoringColors.tooltipTextColor}; font-size: 12px; line-height: 1.4; `
          + 'margin-bottom: 8px; word-wrap: break-word; word-break: break-word; white-space: normal; hyphens: auto;';
        const valueStyle = `color: ${maIroScoringColors.tooltipValueColor}; font-size: 12px; font-weight: 500;`;

        tooltipEl.innerHTML = `
          <div style="${titleStyle}">
            ${title}
          </div>
          ${subtitle ? `<div style="${subtitleStyle}">${subtitle}</div>` : ''}
          <div style="${descStyle}">
            ${formattedDescription}
          </div>
          <div style="${valueStyle}">
            Impact: ${impact}
          </div>
          <div style="${valueStyle}">
            Financial: ${financial}
          </div>
        `;

        const { chart } = event;
        const canvasRect = chart.canvas.getBoundingClientRect();
        const element = chart.getDatasetMeta(activeElement.datasetIndex).data[activeElement.index];
        const { x, y } = element.getProps(['x', 'y'], true);

        const tooltipX = canvasRect.left + x + 20;
        const tooltipY = canvasRect.top + y - 10;

        tooltipEl.style.visibility = 'hidden';
        tooltipEl.style.opacity = '1';

        const tooltipWidth = tooltipEl.offsetWidth;
        const tooltipHeight = tooltipEl.offsetHeight;

        tooltipEl.style.visibility = 'visible';
        tooltipEl.style.opacity = '0';

        const finalX = tooltipX + tooltipWidth > window.innerWidth
          ? canvasRect.left + x - tooltipWidth - 20
          : tooltipX;
        const finalY = tooltipY - tooltipHeight < 0
          ? canvasRect.top + y + 30
          : tooltipY;
        tooltipEl.style.left = `${finalX}px`;
        tooltipEl.style.top = `${finalY}px`;
        tooltipEl.style.opacity = '1';
      }
    },

  }), [t]);

  const backgroundPlugin: Plugin<'scatter'> = useMemo(() => ({
    id: 'backgroundZones',
    beforeDraw: (chart) => {
      const { ctx, chartArea } = chart;
      const {
        left, top, right, bottom,
      } = chartArea;

      ctx.save();
      const yScale = chart.scales.y;
      const middleY = yScale.getPixelForValue(0);
      ctx.fillStyle = maIroScoringColors.opportunityBackground;
      ctx.fillRect(left, top, right - left, middleY - top);
      ctx.fillStyle = maIroScoringColors.riskBackground;
      ctx.fillRect(left, middleY, right - left, bottom - middleY);
      ctx.restore();
    },
    afterDatasetsDraw: (chart) => {
      const { ctx, chartArea } = chart;
      const {
        top, right, bottom,
      } = chartArea;

      ctx.save();
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = maIroScoringColors.opportunityGreen;
      ctx.fillText(t('materialityAssessment.opportunities'), right - 20, top + 30);
      ctx.fillStyle = maIroScoringColors.riskRed;
      ctx.fillText(t('materialityAssessment.risks'), right - 20, bottom - 20);
      ctx.restore();

      const { datasets } = chart.data;
      let datasetIndex = 0;
      while (datasetIndex < datasets.length) {
        const dataset = datasets[datasetIndex];
        const meta = chart.getDatasetMeta(datasetIndex);
        let elementIndex = 0;
        while (elementIndex < meta.data.length) {
          const element = meta.data[elementIndex];
          const { x, y } = element.getProps(['x', 'y'], true);
          const dataPoint = dataset.data[elementIndex];

          if (dataPoint && typeof dataPoint === 'object' && 'label' in dataPoint) {
            ctx.save();
            ctx.fillStyle = maIroScoringColors.labelTextWhite;
            ctx.font = 'bold 12px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText((dataPoint as IChartDataPoint).label, x, y);
            ctx.restore();
          }
          elementIndex += 1;
        }
        datasetIndex += 1;
      }
    },
  }), [t]);

  return {
    chartData,
    chartOptions,
    backgroundPlugin,
  };
};

export default useMAIroChart;
